import React from 'react';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { BILLING_MODE, PAYG_VALIDATION } from '../../../constants';
import './CompanyForm.css';

// Everything here is MODULE-LEVEL and prop-driven. The old FormField / PricingField were
// defined inside the parent component, so every keystroke created a new component type and
// React remounted (and blurred) the input. Do not move these back inside a component.

// Markup below follows the existing forms: BS4 spacing (ml-*), form-control, badge bg-*.

export function CompanyTextField({
  label, name, value, error, onChange, inputRef, type = 'text', placeholder, required = true,
  disabled = false, helpText, children,
}) {
  return (
    <div className="mb-3">
      <label className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {children || (
        <input
          ref={inputRef}
          className={`form-control ${error ? 'is-invalid' : ''}`}
          type={type}
          name={name}
          value={value ?? ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
      {helpText && <div className="form-text text-muted">{helpText}</div>}
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

// `name` is the full dotted key ('pricingPlan.baseCharge' / 'paygPlan.pricePerTopo') so the
// parent's handleInputChange can route it and error[name] lines up.
export function CompanyPricingField({
  label, name, value, error, onChange, inputRef, step = '0.01', min = '0', max, disabled = false,
  hint, prefix, unit, isAddOn = false, isLegacyFree = false, isLocked = false, isDefault = false,
  defaultHint, colClassName = 'col-md-6',
}) {
  const isDisabled = disabled || isLegacyFree || isLocked;
  let title = '';
  if (isLegacyFree) title = 'Foundation Analysis is FREE for legacy companies';
  else if (isLocked) title = 'Pricing updates are disabled for this subscription status';

  const input = (
    <input
      className={`form-control ${error ? 'is-invalid' : ''} ${isDisabled ? 'bg-light' : ''}`}
      type="number"
      ref={inputRef}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      min={min}
      max={max}
      step={step}
      disabled={isDisabled}
      title={title}
    />
  );
  const feedback = error && <div className="invalid-feedback">{error}</div>;

  return (
    <div className={colClassName}>
      <div className="mb-3">
        <label className="form-label">
          {label}
          {isLegacyFree && <span className="badge bg-success ml-2">FREE (Legacy)</span>}
          {isAddOn && <span className="badge bg-info ml-2">Add-On</span>}
          {isDefault && (
            <span className="badge bg-secondary ml-2" title="Using system default value">
              Default
            </span>
          )}
          {isLocked && (
            <span className="badge bg-warning ml-2" title="Pricing updates disabled">
              Locked
            </span>
          )}
        </label>
        {prefix || unit ? (
          // The invalid-feedback must stay a sibling of the input for .is-invalid ~ .invalid-feedback.
          <div className="input-group">
            {prefix && (
              <div className="input-group-prepend">
                <span className="input-group-text">{prefix}</span>
              </div>
            )}
            {input}
            {unit && (
              <div className="input-group-append">
                <span className="input-group-text">{unit}</span>
              </div>
            )}
            {feedback}
          </div>
        ) : (
          <>
            {input}
            {feedback}
          </>
        )}
        {hint && <div className="form-text text-muted">{hint}</div>}
        {isLegacyFree && (
          <div className="form-text text-success">
            <i className="fa fa-gift mr-2"></i>
            FREE for legacy companies
          </div>
        )}
        {defaultHint && !isLocked && !isLegacyFree && <div className="form-text">{defaultHint}</div>}
      </div>
    </div>
  );
}

export function CompanyInvoiceDayField({
  value, error, onChange, inputRef, disabled = false, isDefault = false, isLocked,
  name = 'paygPlan.invoiceDayOfMonth', colClassName = 'col-md-6',
}) {
  const showLocked = isLocked ?? disabled;
  return (
    <div className={colClassName}>
      <div className="mb-3">
        <label className="form-label">
          Invoice Day
          {isDefault && (
            <span className="badge bg-secondary ml-2" title="Using system default value">
              Default
            </span>
          )}
          {showLocked && (
            <span className="badge bg-warning ml-2" title="Pricing updates disabled">
              Locked
            </span>
          )}
        </label>
        <select
          ref={inputRef}
          className={`form-control ${error ? 'is-invalid' : ''} ${disabled ? 'bg-light' : ''}`}
          name={name}
          value={value ?? 1}
          onChange={onChange}
          disabled={disabled}
          title={disabled ? 'Pricing updates are disabled for this subscription status' : ''}
        >
          {[1, 2, 3, 4, 5].map((day) => (
            <option key={day} value={day}>{day}</option>
          ))}
        </select>
        <div className="form-text text-muted">of the month following the usage</div>
        {error && <div className="invalid-feedback">{error}</div>}
      </div>
    </div>
  );
}

// `current` is a step key or a zero-based index. Steps before the current one read as complete.
export function StepIndicator({ steps, current, hasError = {}, onStepClick }) {
  const currentIndex = typeof current === 'number' ? current : steps.findIndex((s) => s.key === current);
  return (
    <ol className="cmp-steps">
      {steps.map((step, index) => {
        const isActive = index === currentIndex;
        const isComplete = index < currentIndex;
        const isError = !!hasError[step.key];
        const state = isError ? 'error' : isActive ? 'active' : isComplete ? 'complete' : '';
        const body = (
          <>
            <span className="cmp-step-circle">
              {isError ? <i className="fa fa-exclamation"></i> : isComplete ? <i className="fa fa-check"></i> : index + 1}
            </span>
            <span className="cmp-step-label">
              {step.icon && <i className={`${step.icon} mr-2`}></i>}
              {step.label}
            </span>
          </>
        );
        return (
          <li key={step.key} className={`cmp-step ${state ? `cmp-step--${state}` : ''}`}>
            {onStepClick ? (
              <button
                type="button"
                className="cmp-step-btn"
                aria-current={isActive ? 'step' : undefined}
                onClick={() => onStepClick(step.key)}
              >
                {body}
              </button>
            ) : (
              <div className="cmp-step-btn" aria-current={isActive ? 'step' : undefined}>{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// Same structure as the tabs on PricingConfig (ul.nav.nav-tabs > li.nav-item > button.nav-link).
export function TabStrip({ tabs, active, onChange }) {
  return (
    <div className="cmp-tabs">
      <ul className="nav nav-tabs">
        {tabs.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${active === tab.key ? 'active' : ''}`}
              onClick={() => onChange(tab.key)}
            >
              {tab.icon && <i className={`${tab.icon} mr-2`}></i>}
              {tab.label}
              {tab.hasError && <span className="cmp-tab-dot cmp-tab-dot--error" title="Has errors" aria-label="Has errors"></span>}
              {!tab.hasError && tab.isModified && (
                <span className="cmp-tab-dot cmp-tab-dot--modified" title="Modified" aria-label="Modified"></span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- validators (same rules and messages as the original AddCompany / UpdateCompany) ----

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validatePersonal({ username, email, phoneNo, countryCode } = {}) {
  const errors = {};

  if (!(username || '').trim()) {
    errors.username = 'Enter a valid username.';
  }

  const trimmedEmail = (email || '').trim();
  if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
    errors.email = 'Enter a valid email.';
  }

  // Phone is optional: only checked when both halves exist.
  if (phoneNo && countryCode) {
    const phoneNumber = parsePhoneNumberFromString(`${countryCode}${phoneNo}`);
    if (!phoneNumber || !phoneNumber.isValid()) {
      errors.phoneNo = 'Please enter a valid phone number';
    }
  }

  return errors;
}

// Deliberately compares the raw values (no Number()), exactly like the original inline code.
export function validateSeatPlan(pricingPlan) {
  const errors = {};
  const plan = pricingPlan || {};

  if (plan.baseCharge < 0 || plan.baseCharge > 10000) {
    errors['pricingPlan.baseCharge'] = 'Base charge must be between 0 and 10,000';
  }

  ['companyUser', 'readWrite', 'topo', 'readOnly'].forEach((field) => {
    if (plan[field] < 0 || plan[field] > 1000) {
      errors[`pricingPlan.${field}`] = `${field} must be between 0 and 1,000`;
    }
  });

  ['foundationAnalysisAddOn', 'floorLevelAddOn'].forEach((field) => {
    if (plan[field] < 0 || plan[field] > 5000) {
      errors[`pricingPlan.${field}`] = `${field} must be between 0 and 5,000`;
    }
  });

  return errors;
}

// PAYG price sheet bounds, mirroring the server. Keys are 'paygPlan.<field>' so they render
// through the same is-invalid / invalid-feedback slot as the seat fields.
export function validatePaygPlan(paygPlan) {
  const errors = {};
  const payg = paygPlan || {};

  const base = Number(payg.baseCharge);
  if (!Number.isFinite(base) || base < PAYG_VALIDATION.MIN_BASE_CHARGE || base > PAYG_VALIDATION.MAX_BASE_CHARGE) {
    errors['paygPlan.baseCharge'] =
      `Base price must be between ${PAYG_VALIDATION.MIN_BASE_CHARGE} and ${PAYG_VALIDATION.MAX_BASE_CHARGE}`;
  }

  const perTopo = Number(payg.pricePerTopo);
  if (!Number.isFinite(perTopo) || perTopo < PAYG_VALIDATION.MIN_PRICE_PER_TOPO || perTopo > PAYG_VALIDATION.MAX_PRICE_PER_TOPO) {
    errors['paygPlan.pricePerTopo'] =
      `Per topo charge must be between ${PAYG_VALIDATION.MIN_PRICE_PER_TOPO} and ${PAYG_VALIDATION.MAX_PRICE_PER_TOPO}`;
  }

  const day = Number(payg.invoiceDayOfMonth);
  if (!Number.isInteger(day) || day < PAYG_VALIDATION.MIN_INVOICE_DAY || day > PAYG_VALIDATION.MAX_INVOICE_DAY) {
    errors['paygPlan.invoiceDayOfMonth'] =
      `Invoice day must be between ${PAYG_VALIDATION.MIN_INVOICE_DAY} and ${PAYG_VALIDATION.MAX_INVOICE_DAY}`;
  }

  return errors;
}

// Only the sheet for the active billing model is validated: the other one is neither shown
// nor submitted, so validating it would block on an invisible error.
export function validateCompanyForm(formData, billingMode) {
  const data = formData || {};
  return {
    ...validatePersonal(data),
    ...(billingMode === BILLING_MODE.PAYG ? validatePaygPlan(data.paygPlan) : validateSeatPlan(data.pricingPlan)),
  };
}
